import { useEffect, useState, useCallback } from "react";
import api, { errMsg, formatTL } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import KPICard from "@/components/app/KPICard";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Scale, Percent } from "lucide-react";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthTitle = (m) => (m ? `${TR_MONTHS[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}` : "");

export default function KarZararPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [company, setCompany] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [tevkifat, setTevkifat] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [sd, s] = await Promise.all([api.get("/service-defs"), api.get("/suppliers")]);
        setCompanies([...new Set(sd.data.map(x => x.company_name))].sort((a, b) => a.localeCompare(b, "tr")));
        const supIds = new Set(sd.data.map(x => x.supplier_id).filter(Boolean));
        setSuppliers(s.data.filter(x => supIds.has(x.id)));
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kar-zarar", {
        params: { month, ...(company ? { company } : {}), ...(supplierId ? { supplier_id: supplierId } : {}), tevkifat },
      });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [month, company, supplierId, tevkifat]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const rows = data?.rows || [];

  const exportSections = data ? [{
    name: "KarZarar", heading: `Kâr Zarar — ${monthTitle(month)}${company ? ` · ${company}` : ""}${tevkifat ? " · Tevkifatlı (Ted. KDV %10)" : ""}`,
    columns: [
      { key: "company_name", label: "Firma" },
      { key: "service", label: "Servis" },
      { key: "vehicle_plate", label: "Araç" },
      { key: "supplier_name", label: "Tedarikçi" },
      { key: "trips", label: "Sefer", format: "number" },
      { key: "firma_tutar", label: "Firma Tutar", format: "money" },
      { key: "firma_kdv", label: "Firma KDV", format: "money" },
      { key: "ted_tutar", label: "Ted. Tutar", format: "money" },
      { key: "ted_kdv", label: `Ted. KDV %${data.ted_rate}`, format: "money" },
      { key: "odenecek_kdv", label: "Ödenecek KDV", format: "money" },
      { key: "kar", label: "Kâr", format: "money" },
      { key: "kar_pct", label: "% Kâr" },
    ],
    rows: [
      ...rows.map(r => ({ ...r, kar_pct: r.kar_pct != null ? `%${r.kar_pct}` : "" })),
      { company_name: "TOPLAM", service: "", vehicle_plate: "", supplier_name: "", trips: t.trips, firma_tutar: t.firma_tutar, firma_kdv: t.firma_kdv, ted_tutar: t.ted_tutar, ted_kdv: t.ted_kdv, odenecek_kdv: t.odenecek_kdv, kar: t.kar, kar_pct: t.kar_pct != null ? `%${t.kar_pct}` : "" },
      ...(data.deduction_total > 0 ? [{ company_name: "KESİNTİLER (+)", service: "", vehicle_plate: "", supplier_name: "", trips: "", firma_tutar: "", firma_kdv: "", ted_tutar: "", ted_kdv: "", odenecek_kdv: "", kar: data.deduction_total, kar_pct: "" },
      { company_name: "NET KÂR", service: "", vehicle_plate: "", supplier_name: "", trips: "", firma_tutar: "", firma_kdv: "", ted_tutar: "", ted_kdv: "", odenecek_kdv: "", kar: t.net_kar, kar_pct: "" }] : []),
    ],
  }] : [];

  return (
    <>
      <PageHeader
        title="Kâr Zarar Raporu"
        subtitle="Puantaj verisinden servis bazlı kâr/zarar — net satış (firma) − net alış (tedarikçi), KDV'siz."
        testid="karzarar-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input type="month" data-testid="kz-month" value={month} onChange={e => setMonth(e.target.value)} className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm num" />
            <select data-testid="kz-company" value={company} onChange={e => setCompany(e.target.value)} className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm max-w-[170px]">
              <option value="">Tüm Firmalar</option>
              {companies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="kz-supplier" value={supplierId} onChange={e => setSupplierId(e.target.value)} className="px-2.5 py-2 bg-white border border-[#E5E5E5] text-sm max-w-[170px]">
              <option value="">Tüm Tedarikçiler</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none bg-white border border-[#E5E5E5] px-2.5 py-2" title="Tevkifat uygulanırsa tedarikçi KDV'si %20 yerine %10 hesaplanır">
              <input type="checkbox" data-testid="kz-tevkifat" checked={tevkifat} onChange={e => setTevkifat(e.target.checked)} />
              Tevkifat (Ted. KDV %20→%10)
            </label>
          </div>
        }
      />

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KPICard label="Firma Toplam (Net)" value={formatTL(t.firma_tutar)} icon={TrendingUp} testid="kz-kpi-firma" />
          <KPICard label="Tedarikçi Toplam (Net)" value={formatTL(t.ted_tutar)} icon={TrendingDown} testid="kz-kpi-ted" />
          <KPICard label="Ödenecek KDV" value={formatTL(t.odenecek_kdv)} icon={Scale} testid="kz-kpi-kdv" />
          <KPICard
            label={`Net Kâr${data.deduction_total > 0 ? " (kesinti dahil)" : ""}`}
            value={formatTL(t.net_kar)}
            sub={t.kar_pct != null ? `%${t.kar_pct} kâr marjı` : undefined}
            icon={Percent}
            accent={t.net_kar >= 0 ? "success" : "error"}
            testid="kz-kpi-kar"
          />
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-md" data-testid="kz-table-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
          <div className="font-bold font-heading">{monthTitle(month)} — {rows.length} servis</div>
          {rows.length > 0 && (
            <ExportButtons
              testidPrefix="kz-export"
              filename={`kar_zarar_${month}${company ? `_${company.toLocaleLowerCase("tr-TR").replace(/[^a-z0-9]/g, "_")}` : ""}`}
              title={`KÂR ZARAR RAPORU — ${monthTitle(month)}`}
              subtitle={`${company || "Tüm firmalar"} · ${tevkifat ? "Tevkifatlı (Ted. KDV %10)" : "Ted. KDV %20"} · Net Kâr: ${formatTL(t?.net_kar || 0)}`}
              sections={exportSections}
            />
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA]">
              <tr>
                {["Firma", "Servis", "Araç", "Tedarikçi", "Sefer", "Firma Tutar", `Firma KDV`, "Ted. Tutar", `Ted. KDV %${data?.ted_rate ?? 20}`, "Ödenecek KDV", "Kâr", "% Kâr"].map((h, i) => (
                  <th key={i} className={`px-2.5 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold whitespace-nowrap ${i >= 4 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-t border-[#F5F5F5] ${r.kar < 0 ? "bg-[#FEF2F2]" : ""}`} data-testid={`kz-row-${i}`}>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.company_name}</td>
                  <td className="px-2.5 py-1.5 font-semibold">{r.service}</td>
                  <td className="px-2.5 py-1.5 num whitespace-nowrap">{r.vehicle_plate || "-"}</td>
                  <td className="px-2.5 py-1.5">{r.supplier_name}</td>
                  <td className="px-2.5 py-1.5 text-right num font-bold">{r.trips}</td>
                  <td className="px-2.5 py-1.5 text-right num">{formatTL(r.firma_tutar)}</td>
                  <td className="px-2.5 py-1.5 text-right num text-neutral-500">{formatTL(r.firma_kdv)} <span className="text-[9px]">%{r.firma_vat_pct}</span></td>
                  <td className="px-2.5 py-1.5 text-right num">{formatTL(r.ted_tutar)}</td>
                  <td className="px-2.5 py-1.5 text-right num text-neutral-500">{formatTL(r.ted_kdv)}</td>
                  <td className="px-2.5 py-1.5 text-right num">{formatTL(r.odenecek_kdv)}</td>
                  <td className={`px-2.5 py-1.5 text-right num font-bold ${r.kar < 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(r.kar)}</td>
                  <td className={`px-2.5 py-1.5 text-right num font-semibold ${r.kar < 0 ? "text-[#DC2626]" : ""}`}>{r.kar_pct != null ? `%${r.kar_pct}` : "-"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-8 text-center text-neutral-400">Bu dönem için puantaj verisi yok — önce Servis Puantajı ekranından puantaj girin.</td></tr>
              )}
              {rows.length > 0 && (
                <tr className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA] font-bold">
                  <td className="px-2.5 py-2 text-[10px] uppercase tracking-widest" colSpan={4}>Toplam</td>
                  <td className="px-2.5 py-2 text-right num" data-testid="kz-total-trips">{t.trips}</td>
                  <td className="px-2.5 py-2 text-right num">{formatTL(t.firma_tutar)}</td>
                  <td className="px-2.5 py-2 text-right num text-neutral-500">{formatTL(t.firma_kdv)}</td>
                  <td className="px-2.5 py-2 text-right num">{formatTL(t.ted_tutar)}</td>
                  <td className="px-2.5 py-2 text-right num text-neutral-500">{formatTL(t.ted_kdv)}</td>
                  <td className="px-2.5 py-2 text-right num">{formatTL(t.odenecek_kdv)}</td>
                  <td className={`px-2.5 py-2 text-right num ${t.kar < 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`} data-testid="kz-total-kar">{formatTL(t.kar)}</td>
                  <td className="px-2.5 py-2 text-right num">{t.kar_pct != null ? `%${t.kar_pct}` : "-"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Kesintiler — kârı artırır */}
        {data?.deductions?.length > 0 && (
          <div className="px-5 py-3 border-t border-[#E5E5E5] text-xs" data-testid="kz-deductions">
            <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mr-3">Tedarikçi Kesintileri (kârı artırır)</span>
            {data.deductions.map((d) => (
              <span key={d.id} className="inline-flex items-center gap-1 mr-4">
                <span className="font-semibold">{d.supplier_name}:</span> {d.description}
                <b className="num text-[#16A34A]">+{formatTL(d.amount)}</b>
              </span>
            ))}
            <span className="ml-2">→ Net Kâr: <b className="num text-[#16A34A]" data-testid="kz-net-kar">{formatTL(t.net_kar)}</b></span>
          </div>
        )}
      </div>
    </>
  );
}
